import React, { useRef } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { Checkbox, FormField, Select, TextField } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AddMoneyCircleIcon,
  BankIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { ArrowRight02Icon } from "@hugeicons-pro/core-solid-sharp";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import type { BankAccount } from "hooks/useGetBankAccounts";
import { Inline, Stack } from "components/layout/reusableLayouts";
import BankIntegration from "components/admin/BankIntegration";
import CurrencyInput from "components/admin/common/CurrencyInput";
import TransferTermsAndConditions from "components/admin/common/TransferTermsAndConditions";

/**
 * The fields the three money-movement drawers (Add Funds, Withdraw, Auto
 * Balance) had a copy of each, down to the same white `styled(Select)` and the
 * same absolutely-positioned character counter. One copy each, here.
 */

/** The memo the API accepts. Enforced on input, not on submit. */
const MEMO_MAX_LENGTH = 250;

/**
 * An account's one-line name. The three drawers disagreed (two showed
 * `nickname last4`, one `bankName last4`); this takes whichever name the
 * account actually has, so an external bank still reads as its bank. The last4
 * rides on the end because the Select truncates in the MIDDLE, which is what
 * keeps the digits visible on a long name.
 */
export const accountName = (account: BankAccount): string => {
  const name =
    account.nickname || titleCase((account.bankName || "").toLowerCase());
  return [name, account.last4].filter(Boolean).join(" ");
};

interface AccountSelectProps {
  name: string;
  /**
   * Visible label. Omit it where the design shows the two pickers bare and the
   * arrow between them carries the direction; the name then rides on
   * `aria-label` so a screen reader still hears which one it is.
   */
  label?: string;
  /** Accessible name when `label` is omitted. */
  ariaLabel?: string;
  value: string;
  accounts: BankAccount[];
  onSelect: (accountId: string) => void;
  errorText?: string | null;
  /**
   * Offer an "Add New Account" row under the options. Set both to enable it:
   * the caller has to say where the new account id goes.
   */
  onAccountAdded?: (accountId: string) => void;
  refetchAccounts?: () => void;
  disabled?: boolean;
}

/** One account picker: glyph in the field, accounts as options, add-a-bank row. */
export const AccountSelect: React.FC<AccountSelectProps> = ({
  name,
  label,
  ariaLabel,
  value,
  accounts,
  onSelect,
  errorText,
  onAccountAdded,
  refetchAccounts,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const canAddAccount = !!onAccountAdded && !!refetchAccounts;

  // BankIntegration only exposes its opener through a render prop, and the
  // Select's bottom action takes a handler rather than a node. Stashing the
  // opener as it renders is what lets the DS row drive it; a ref rather than
  // state because nothing here should re-render on it.
  const openAddBank = useRef<() => void>(() => {});

  return (
    <>
      <Select
        id={name}
        label={label}
        aria-label={label ? undefined : (ariaLabel ?? name)}
        required
        fullWidth
        disabled={disabled}
        errorText={errorText ?? undefined}
        value={value || null}
        onChange={(selected) => onSelect(selected ?? "")}
        leading={<HugeiconsIcon icon={BankIcon} size={16} />}
        // The distinguishing part of an account label is the last4 at the end.
        truncateMiddle
        options={accounts?.map((account) => ({
          id: account.id,
          label: accountName(account),
        }))}
        bottomAction={
          canAddAccount
            ? {
                label: t("Add New Account"),
                icon: <HugeiconsIcon icon={AddMoneyCircleIcon} size={16} />,
                onClick: () => openAddBank.current(),
              }
            : undefined
        }
      />

      {canAddAccount && (
        <BankIntegration
          refetchBankAccounts={refetchAccounts}
          onBankAdded={onAccountAdded}
          renderTrigger={(handleOpenAddBank) => {
            openAddBank.current = handleOpenAddBank;
            return null;
          }}
        />
      )}
    </>
  );
};

interface TransferAccountsRowProps {
  from: React.ReactNode;
  to: React.ReactNode;
}

/** Source, arrow, destination. */
export const TransferAccountsRow: React.FC<TransferAccountsRowProps> = ({
  from,
  to,
}) => (
  <Inline
    gap="var(--space-3)"
    align="center"
    wrap={false}
    sx={{ width: "100%", "& > *": { minWidth: 0 } }}
  >
    {from}
    <Typography
      component="span"
      aria-hidden
      sx={{ color: "var(--color-text-tertiary)", flexShrink: 0 }}
    >
      <HugeiconsIcon icon={ArrowRight02Icon} size={16} />
    </Typography>
    {to}
  </Inline>
);

interface AmountFieldProps {
  value: string;
  onChange: (raw: number) => void;
  /** How long the money takes to land. Differs per drawer. */
  helperText: string;
  errorText?: string | null;
}

/** The amount to move, in the company's currency. */
export const AmountField: React.FC<AmountFieldProps> = ({
  value,
  onChange,
  helperText,
  errorText,
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      label={t("Amount")}
      htmlFor="amount"
      required
      helperText={helperText}
      errorText={errorText}
      sx={{ maxWidth: 320 }}
    >
      <CurrencyInput
        id="amount"
        name="amount"
        size="md"
        fullWidth
        value={Number(value)}
        error={!!errorText}
        onChange={onChange}
      />
    </FormField>
  );
};

interface MemoFieldProps {
  value: string;
  onChange: (memo: string) => void;
  /** Defaults to "Memo"; Auto Balance names its own. */
  label?: string;
  /** Defaults to "Comment". */
  placeholder?: string;
  disabled?: boolean;
}

/** Free text on the transfer, with the API's length cap shown as it fills. */
export const MemoField: React.FC<MemoFieldProps> = ({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    // The counter sits inside the field's bottom right rather than below it,
    // so the memo reads as one box. Absolute over the field because the DS
    // TextField has no slot inside its multiline input.
    <Box sx={{ position: "relative", width: "100%" }}>
      <TextField
        id="memo"
        name="memo"
        label={label ?? t("Memo")}
        placeholder={(placeholder ?? t("Comment")) as string}
        fullWidth
        multiline
        rows={3}
        disabled={disabled}
        value={value}
        onChange={(event) => {
          if (event.target.value.length <= MEMO_MAX_LENGTH) {
            onChange(event.target.value);
          }
        }}
      />
      <Typography
        variant="caption"
        aria-hidden
        sx={{
          position: "absolute",
          right: "var(--space-4)",
          bottom: "var(--space-3)",
          color: "var(--color-text-tertiary)",
          pointerEvents: "none",
        }}
      >
        {`${value.length}/${MEMO_MAX_LENGTH}`}
      </Typography>
    </Box>
  );
};

interface ConfirmTransferProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  errorText?: string | null;
  disabled?: boolean;
  /**
   * What the tick authorizes. Defaults to the neutral "Confirm Transfer",
   * because the three drawers do not authorize the same thing: pulling from a
   * customer's bank is an ACH debit, and paying out to it is not.
   */
  label?: React.ReactNode;
}

/** The confirmation gate and the terms it confirms. */
export const ConfirmTransfer: React.FC<ConfirmTransferProps> = ({
  checked,
  onChange,
  errorText,
  disabled = false,
  label,
}) => {
  const { t } = useTranslation();

  return (
    <Stack gap="var(--space-3)">
      <FormControlLabel
        name="confirmTransfer"
        checked={checked}
        disabled={disabled}
        onChange={(_event, isChecked) => onChange(isChecked)}
        control={<Checkbox />}
        // A multi-line authorization keeps the box on its first line rather
        // than floating in the middle of the paragraph.
        sx={{ alignItems: "flex-start", marginInline: 0 }}
        slotProps={{ typography: { sx: { paddingTop: "var(--space-2)" } } }}
        label={
          <Typography variant="body2">
            {label ?? t("Confirm Transfer")}
          </Typography>
        }
      />

      {errorText && (
        <Typography
          variant="caption"
          sx={{ color: "var(--color-text-danger)" }}
        >
          {errorText}
        </Typography>
      )}

      <Divider />
      <TransferTermsAndConditions />
    </Stack>
  );
};
